import { createInertiaApp, router } from '@inertiajs/react'
import * as Sentry from '@sentry/react'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { Toaster, toast } from 'sonner'

import '../styles/application.css'

const sentryDsn = document.querySelector<HTMLMetaElement>('meta[name="sentry-dsn"]')?.content
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: document.querySelector<HTMLMetaElement>('meta[name="rails-env"]')?.content || 'production',
    tracesSampleRate: 0.1,
  })
}

router.on('invalid', (event) => {
  const response = event.detail.response
  if (response.status === 401 || response.status === 419) {
    event.preventDefault()
    window.location.href = '/login'
  }
})

const pages = import.meta.glob('../pages/**/*.tsx')

async function resolvePage(name: string) {
  const path = `../pages/${name}.tsx`
  const resolver = pages[path]
  if (!resolver) {
    throw new Error(`Page not found: ${name}`)
  }
  const page = (await resolver()) as { default: React.ComponentType<unknown> }
  return page.default
}

// Show flash toasts only on fresh server responses (the 'success' event).
// The 'navigate' event fires on back/forward too, which would re-show stale flash.
router.on('success', (event) => {
  const flash = (event.detail.page.props as Record<string, unknown>).flash as Record<string, string> | undefined
  if (!flash) return

  if (flash.success) toast.success(flash.success)
  if (flash.notice) toast.success(flash.notice)
  if (flash.error) toast.error(flash.error)
  if (flash.alert) toast.error(flash.alert)
})

createInertiaApp({
  resolve: resolvePage,
  setup({ el, App, props }) {
    const root = createRoot(el)
    root.render(
      <React.StrictMode>
        <App {...props} />
        <Toaster richColors position="top-right" closeButton />
      </React.StrictMode>,
    )
  },
  progress: {
    color: '#1232D7',
    showSpinner: true,
  },
})
